if (!customElements.get('uno-purchase-actions')) {
  customElements.define('uno-purchase-actions', class UnoPurchaseActions extends HTMLElement {
    constructor() {
      super();
      this.form = null;
      this.addButton = null;
      this.buyButton = null;
      this.sticky = null;
      this.stickyAdd = null;
      this.stickyBuy = null;
      this.priceSource = null;
      this.priceObserver = null;
      this.buttonObserver = null;
      this.visibilityObserver = null;
      this.onFormChange = this.sync.bind(this);
    }

    connectedCallback() {
      if (this.dataset.ready === '1') return;
      this.dataset.ready = '1';
      this.form = this.querySelector('form[data-type="add-to-cart-form"]');
      this.addButton = this.querySelector('[data-uno-main-add]');
      this.buyButton = this.querySelector('[data-uno-main-buy]');
      this.sticky = this.querySelector('[data-uno-purchase-sticky]');
      this.stickyAdd = this.querySelector('[data-uno-sticky-add]');
      this.stickyBuy = this.querySelector('[data-uno-sticky-buy]');
      const sectionId = this.dataset.sectionId;
      const root = sectionId ? document.getElementById(`MainProduct-${sectionId}`) : this.closest('product-info');
      this.priceSource = root?.querySelector(`#price-${sectionId}`) || root?.querySelector('[id^="price-"]');
      if (!this.form || !this.addButton || !this.buyButton) return;
      this.buyButton.addEventListener('click', () => this.buyNow());
      this.stickyAdd?.addEventListener('click', () => this.addButton.click());
      this.stickyBuy?.addEventListener('click', () => this.buyButton.click());
      this.form.addEventListener('change', this.onFormChange);
      this.form.addEventListener('input', this.onFormChange);
      this.buttonObserver = new MutationObserver(() => this.sync());
      this.buttonObserver.observe(this.addButton, { attributes: true, subtree: true, childList: true, attributeFilter: ['disabled', 'aria-disabled', 'class'] });
      if (this.priceSource) {
        this.priceObserver = new MutationObserver(() => this.syncPrice());
        this.priceObserver.observe(this.priceSource, { subtree: true, childList: true, characterData: true });
      }
      if (this.sticky && 'IntersectionObserver' in window) {
        const actionRow = this.querySelector('[data-uno-purchase-row]');
        this.visibilityObserver = new IntersectionObserver(([entry]) => {
          if (window.matchMedia('(min-width: 750px)').matches) {
            this.sticky.classList.remove('is-visible');
            this.sticky.setAttribute('aria-hidden', 'true');
            return;
          }
          const visible = !entry.isIntersecting && window.scrollY > 120;
          this.sticky.classList.toggle('is-visible', visible);
          this.sticky.setAttribute('aria-hidden', visible ? 'false' : 'true');
        }, { threshold: 0.08 });
        if (actionRow) this.visibilityObserver.observe(actionRow);
      }
      this.sync();
    }

    disconnectedCallback() {
      this.form?.removeEventListener('change', this.onFormChange);
      this.form?.removeEventListener('input', this.onFormChange);
      this.buttonObserver?.disconnect();
      this.priceObserver?.disconnect();
      this.visibilityObserver?.disconnect();
    }

    isAvailable() {
      const variantInput = this.form?.querySelector('[name="id"]');
      return !!(variantInput && !variantInput.disabled && variantInput.value && !this.addButton.disabled && this.addButton.getAttribute('aria-disabled') !== 'true');
    }

    syncPrice() {
      const price = this.priceSource?.querySelector('.price-item--sale, .price-item--regular')?.textContent?.trim();
      const target = this.querySelector('[data-uno-sticky-price]');
      if (price && target) target.textContent = price;
    }

    sync() {
      const available = this.isAvailable();
      if (this.buyButton.dataset.busy !== '1') this.buyButton.disabled = !available;
      if (this.stickyAdd) this.stickyAdd.disabled = !available;
      if (this.stickyBuy && this.buyButton.dataset.busy !== '1') this.stickyBuy.disabled = !available;
      const addLabel = this.addButton.querySelector('span')?.textContent?.trim() || this.addButton.textContent.trim();
      const stickyAddLabel = this.querySelector('[data-uno-sticky-add-label]');
      if (stickyAddLabel && addLabel) stickyAddLabel.textContent = addLabel;
      this.syncPrice();
    }

    showError(message) {
      const productForm = this.closest('product-form') || this.querySelector('product-form');
      const wrapper = productForm?.querySelector('.product-form__error-message-wrapper') || this.querySelector('.product-form__error-message-wrapper');
      const text = wrapper?.querySelector('.product-form__error-message');
      if (wrapper && text) {
        text.textContent = message || this.dataset.errorMessage || 'Could not start checkout. Please try again.';
        wrapper.hidden = false;
      }
    }

    async reconcileGiftCap() {
      if (window.__unoGiftCapReady && typeof window.__unoGiftCapReady.then === 'function') await window.__unoGiftCapReady;
      if (window.UnoGiftCap && typeof window.UnoGiftCap.reconcile === 'function') await window.UnoGiftCap.reconcile();
    }

    async evaluateShipping() {
      const shipping = window.UnoShippingUpgrade;
      if (!shipping || typeof shipping.evaluateFreeShippingBeforeCheckout !== 'function') return true;
      return shipping.evaluateFreeShippingBeforeCheckout({ checkoutUrl: this.dataset.checkoutUrl || '/checkout', reconcileFirst: false });
    }

    checkoutNow() {
      const shipping = window.UnoShippingUpgrade;
      if (shipping && typeof shipping.checkoutNow === 'function') {
        shipping.checkoutNow();
        return;
      }
      const checkoutForm = document.createElement('form');
      checkoutForm.method = 'post';
      checkoutForm.action = window.routes?.cart_url || '/cart';
      checkoutForm.style.display = 'none';
      const checkoutInput = document.createElement('input');
      checkoutInput.type = 'hidden';
      checkoutInput.name = 'checkout';
      checkoutInput.value = 'checkout';
      checkoutForm.appendChild(checkoutInput);
      document.body.appendChild(checkoutForm);
      checkoutForm.submit();
    }

    releaseBuyNow() {
      this.buyButton.dataset.busy = '0';
      this.buyButton.classList.remove('is-loading');
      this.sync();
    }

    async buyNow() {
      if (!this.isAvailable() || this.buyButton.dataset.busy === '1') return;
      if (typeof this.form.checkValidity === 'function' && !this.form.checkValidity()) {
        this.form.reportValidity?.();
        return;
      }
      this.buyButton.dataset.busy = '1';
      this.buyButton.disabled = true;
      this.buyButton.classList.add('is-loading');
      if (this.stickyBuy) this.stickyBuy.disabled = true;
      try {
        const response = await fetch(this.dataset.cartAddUrl || '/cart/add.js', {
          method: 'POST',
          headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          credentials: 'same-origin',
          body: new FormData(this.form)
        });
        let payload = null;
        try { payload = await response.json(); } catch (_) {}
        if (!response.ok || payload?.status) throw new Error(payload?.description || payload?.message || this.dataset.errorMessage);
        await this.reconcileGiftCap();
        const proceed = await this.evaluateShipping();
        if (proceed) {
          this.checkoutNow();
          return;
        }
        this.releaseBuyNow();
      } catch (error) {
        this.showError(error?.message || this.dataset.errorMessage);
        this.releaseBuyNow();
      }
    }
  });
}
